import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertOctagon, Home, Loader2, RotateCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/queryClient";
import { getConnectionState, subscribeToConnectionState } from "@/lib/server-status";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  showHomeButton?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
  connectionState: "online" | "reconnecting";
}

const MAX_AUTO_RETRIES = 1;

function isServerRelatedError(error: Error | null): boolean {
  if (!error) return false;
  if (error instanceof TypeError && error.message.toLowerCase().includes("fetch")) return true;
  if (error instanceof ApiError && (error.status === 502 || error.status === 503 || error.status === 504)) return true;
  const msg = error.message;
  if (/fetch|network|failed to fetch|networkerror/i.test(msg)) return true;
  const match = msg.match(/^(\d+):/);
  if (match) {
    const status = parseInt(match[1], 10);
    return status === 502 || status === 503 || status === 504;
  }
  return false;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private unsubscribe: (() => void) | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
      connectionState: getConnectionState(),
    };
  }

  componentDidMount() {
    this.unsubscribe = subscribeToConnectionState((connectionState) => {
      this.setState({ connectionState });
      if (connectionState === "online" && this.state.hasError && isServerRelatedError(this.state.error)) {
        setTimeout(() => {
          this.setState({ hasError: false, error: null });
        }, 500);
      }
    });
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isServerRelatedError(error)) {
      console.warn("[ErrorBoundary] Server unavailable — auto-retrying in 3s", error.message);
      setTimeout(() => {
        this.setState({ hasError: false, error: null });
      }, 3_000);
      return;
    }
    console.error("[ErrorBoundary]", error, info.componentStack);
    if (this.state.retryCount < MAX_AUTO_RETRIES) {
      setTimeout(() => {
        this.setState(prev => ({ hasError: false, error: null, retryCount: prev.retryCount + 1 }));
      }, 100);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError && this.state.retryCount >= MAX_AUTO_RETRIES) {
      const isServerDown =
        this.state.connectionState === "reconnecting" ||
        isServerRelatedError(this.state.error);

      if (isServerDown) {
        return (
          <div
            className="flex flex-col items-center justify-center min-h-[400px] gap-4 p-8 text-center"
            data-testid="error-boundary-server-unavailable"
          >
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-yellow-500/10 border border-yellow-500/20">
              <WifiOff className="w-7 h-7 text-yellow-400 animate-pulse" />
            </div>
            <div className="space-y-2 max-w-sm">
              <h2 className="text-lg font-bold font-mono tracking-tight text-white/80">
                Reconnecting…
              </h2>
              <p className="text-sm text-white/40 font-mono">
                The server is temporarily unavailable. Retrying automatically.
              </p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
              </div>
            </div>
            <Button
              onClick={this.handleRetry}
              variant="outline"
              className="gap-2"
              data-testid="button-error-retry"
            >
              <RotateCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        );
      }

      const title = this.props.fallbackTitle || "Something went wrong";
      return (
        <div
          className="flex flex-col items-center justify-center min-h-[400px] gap-6 p-8 text-center"
          data-testid="error-boundary-fallback"
        >
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-red-500/10 border border-red-500/20">
            <AlertOctagon className="w-8 h-8 text-red-400" />
          </div>
          <div className="space-y-2 max-w-md">
            <h2 className="text-xl font-bold font-mono tracking-tight text-white/90">
              {title}
            </h2>
            <p className="text-sm text-white/50 font-mono">
              An unexpected error occurred. You can try again or return to the home page.
            </p>
            {this.state.error && (
              <p className="text-xs text-red-400/60 font-mono mt-2 break-all">
                A rendering error occurred. Please try again.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={this.handleRetry}
              variant="outline"
              className="gap-2"
              data-testid="button-error-retry"
            >
              <RotateCw className="w-4 h-4" />
              Try Again
            </Button>
            {this.props.showHomeButton !== false && (
              <Button
                onClick={this.handleGoHome}
                variant="ghost"
                className="gap-2 text-white/50 hover:text-white/80"
                data-testid="button-error-home"
              >
                <Home className="w-4 h-4" />
                Go Home
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}
